import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

type OverviewAgency = {
  id: string;
  name: string;
  createdAt: string;
  balance: number;
  balanceUpdatedAt: string | null;
};

type LedgerEntry = {
  id: string;
  amount: number;
  balanceAfter: number;
  type:
    | 'ADMIN_GRANT'
    | 'AI_VIDEO_EDIT_USAGE'
    | 'REMOTION_STUDIO_USAGE'
    | 'ANIMATION_STUDIO_EXPORT_USAGE'
    | 'ANIMATION_STUDIO_REFINE_PROMPT_USAGE';
  description: string | null;
  createdAt: string;
  sourceAgentJobId: string | null;
  createdBy: { id: string; fullName: string | null } | null;
  descriptCutJob: { id: string; uploadProjectName: string | null } | null;
};

export default function AdminAiCredits() {
  const [agencies, setAgencies] = useState<OverviewAgency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerBalance, setLedgerBalance] = useState(0);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [ledgerError, setLedgerError] = useState<string | null>(null);

  const [grantAmount, setGrantAmount] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);

  const loadOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get<{ agencies: OverviewAgency[] }>('/admin/ai-credits/overview');
      setAgencies(data.agencies || []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOverview();
  }, []);

  const loadLedger = async (agencyId: string) => {
    try {
      setLedgerLoading(true);
      setLedgerError(null);
      const { data } = await api.get<{
        balance: number;
        entries: LedgerEntry[];
      }>(`/admin/agencies/${agencyId}/ai-credits/ledger`);
      setLedgerBalance(data.balance);
      setLedgerEntries(data.entries || []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setLedgerError(err.response?.data?.error || 'Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedId) {
      setLedgerEntries([]);
      return;
    }
    void loadLedger(selectedId);
  }, [selectedId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agencies;
    return agencies.filter((a) => a.name.toLowerCase().includes(q));
  }, [agencies, search]);

  const totalBalance = useMemo(() => agencies.reduce((s, a) => s + a.balance, 0), [agencies]);

  const onGrant = async () => {
    if (!selectedId) return;
    const n = Number(grantAmount);
    if (!Number.isFinite(n) || n <= 0) {
      setLedgerError('Enter a positive amount');
      return;
    }
    try {
      setGranting(true);
      setLedgerError(null);
      await api.post(`/admin/agencies/${selectedId}/ai-credits/grant`, {
        amount: Math.floor(n),
        description: grantNote.trim() || undefined,
      });
      setGrantAmount('');
      setGrantNote('');
      await loadOverview();
      await loadLedger(selectedId);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setLedgerError(err.response?.data?.error || 'Grant failed');
    } finally {
      setGranting(false);
    }
  };

  const selectedAgency = agencies.find((a) => a.id === selectedId);

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">AI edit credits</h1>
        <p className="text-glass-text-2 text-sm mt-1">
          Grant platform credits to agencies. Usage is deducted after each successful AI video edit step (same units
          as the provider&apos;s AI meter when reported).
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Agencies</div>
          <div className="stat-display text-2xl mt-1">{agencies.length}</div>
        </div>
        <div className="glass-card p-5 md:col-span-2">
          <div className="text-sm text-glass-text-2">Total credits on platform (sum of balances)</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{totalBalance}</div>
        </div>
      </div>

      <div className="glass-card p-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agency name"
          className="glass-input w-full px-4 py-2"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="glass-card overflow-hidden">
          <div className="px-4 py-3 border-b border-glass font-medium text-glass-text">Agency balances</div>
          {loading ? (
            <p className="p-6 text-glass-text-2">Loading…</p>
          ) : (
            <ul className="divide-y divide-glass max-h-[480px] overflow-y-auto">
              {filtered.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(a.id)}
                    className={`w-full text-left px-4 py-3 flex justify-between gap-2 hover:bg-glass-3/40 transition-colors ${
                      selectedId === a.id ? 'bg-accent2/10' : ''
                    }`}
                  >
                    <span className="font-medium truncate text-glass-text">{a.name}</span>
                    <span className="shrink-0 text-accent2 font-mono">{a.balance}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="glass-card p-4 space-y-4">
          {!selectedId ? (
            <p className="text-glass-text-2 text-sm">Select an agency to grant credits and view the ledger.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <h2 className="page-title text-lg">{selectedAgency?.name}</h2>
                  <Link to={`/admin/agencies/${selectedId}`} className="text-sm text-accent2 hover:underline">
                    Open agency profile
                  </Link>
                </div>
                <div className="text-right">
                  <div className="text-xs text-glass-text-3">Current balance</div>
                  <div className="stat-display text-2xl font-mono text-accent2">
                    {ledgerLoading ? '…' : ledgerBalance}
                  </div>
                </div>
              </div>

              <div className="glass-card-tight p-3 space-y-2">
                <div className="text-sm font-medium text-glass-text">Grant credits</div>
                <div className="flex flex-wrap gap-2">
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                    placeholder="Amount"
                    className="glass-input flex-1 min-w-[100px] px-3 py-2"
                  />
                  <button
                    type="button"
                    onClick={onGrant}
                    disabled={granting}
                    className="btn-accent px-4 py-2 text-sm font-medium disabled:opacity-50"
                  >
                    {granting ? 'Saving…' : 'Add credits'}
                  </button>
                </div>
                <input
                  type="text"
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="Note (optional)"
                  className="glass-input w-full px-3 py-2 text-sm"
                />
              </div>

              {ledgerError && <div className="text-sm text-red-500">{ledgerError}</div>}

              <div>
                <div className="text-sm font-medium text-glass-text mb-2">Ledger (newest first)</div>
                {ledgerLoading ? (
                  <p className="text-glass-text-2 text-sm">Loading ledger…</p>
                ) : ledgerEntries.length === 0 ? (
                  <p className="text-glass-text-2 text-sm">No entries yet.</p>
                ) : (
                  <div className="overflow-x-auto max-h-80 overflow-y-auto glass-card-tight">
                    <table className="min-w-full text-sm">
                      <thead className="border-b border-glass bg-glass-2/30 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-glass-text-3">When</th>
                          <th className="text-left px-3 py-2 text-glass-text-3">Type</th>
                          <th className="text-right px-3 py-2 text-glass-text-3">Δ</th>
                          <th className="text-right px-3 py-2 text-glass-text-3">After</th>
                          <th className="text-left px-3 py-2 text-glass-text-3">Detail</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-glass">
                        {ledgerEntries.map((row) => (
                          <tr key={row.id} className="hover:bg-glass-3/30">
                            <td className="px-3 py-2 whitespace-nowrap text-glass-text-2">
                              {new Date(row.createdAt).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-glass-text">
                              {row.type === 'ADMIN_GRANT'
                                ? 'Admin grant'
                                : row.type === 'REMOTION_STUDIO_USAGE'
                                  ? 'Animation studio — generate'
                                  : row.type === 'ANIMATION_STUDIO_EXPORT_USAGE'
                                    ? 'Animation studio — export'
                                    : row.type === 'ANIMATION_STUDIO_REFINE_PROMPT_USAGE'
                                      ? 'Animation studio — refine prompt'
                                      : 'AI video edit'}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-glass-text">
                              {row.amount > 0 ? `+${row.amount}` : row.amount}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-glass-text">{row.balanceAfter}</td>
                            <td className="px-3 py-2 max-w-[200px] truncate text-glass-text-2" title={row.description || ''}>
                              {row.description}
                              {row.createdBy?.fullName && (
                                <span className="text-glass-text-3"> · by {row.createdBy.fullName}</span>
                              )}
                              {row.descriptCutJob && (
                                <span className="block text-xs text-glass-text-3 truncate">
                                  Job: {row.descriptCutJob.uploadProjectName || row.descriptCutJob.id.slice(0, 8)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
